import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Menu, X } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { PrimaryButton, SecondaryButton } from "@/components/flow/Primitives";
import { BrandWordmark } from "@/components/flow/Brand";
import { AnimatePresence, motion } from "framer-motion";
import { EASE } from "@/lib/motion";
import { t } from "@/lib/i18n";
import { cn } from "@/lib/utils";

/* ---------------------------------------------------------------------------
   Site chrome shared by the marketing pages (home, pricing).
--------------------------------------------------------------------------- */

export const CONTAINER = "max-w-[1320px] mx-auto px-5 sm:px-8 lg:px-12";
export const EYEBROW = "eyebrow-pill inline-flex items-center gap-2 rounded-full border border-hairline bg-surface/80 pl-2.5 pr-3 h-7 text-[12px] font-medium text-ink/70";
export const H2 = "mt-5 font-display text-[38px] sm:text-[54px] lg:text-[64px] text-ink";
export const SUB = "mt-5 text-[16px] sm:text-[17.5px] text-ink/60 leading-[1.6]";

/* The header is deliberately simple: wordmark · Features · Pricing · Log in · Get started. No anchor links. */
export const NAV = [
  { href: "/features", key: "lp.nav.features", id: "features", route: true },
  { href: "/pricing", key: "lp.nav.pricing", id: "pricing", route: true },
];
/* The mobile menu also lists the page's sections — the desktop header stays anchor-free on purpose. */
export const ON_PAGE = [
  ["how", "hiw.eyebrow"],
  ["story", "l12.story.eyebrow"],
  ["problem", "lp.nav.problem"],
  ["capabilities", "lp.nav.capabilities"],
  ["whitelabel", "lp.nav.whitelabel"],
  ["integrations", "lp.nav.integrations"],
  ["faq", "lp.nav.faq"],
];
/* Sections of /features for the mobile menu. */
export const FEATURES_ON_PAGE = [
  ["your-site", "ft.site.nav"],
  ["journey", "ft.journey.eyebrow"],
  ["modules", "ft.mod.eyebrow"],
  ["included", "ft.inc.eyebrow"],
];

function NavLink({ item, className, onClick, children, testId, dark = false, ...rest }) {
  const { pathname } = useLocation();
  const active = pathname === item.href;
  return (
    <Link to={item.href} onClick={onClick} className={cn(className, active && (dark ? "text-white" : "text-ink"))} aria-current={active ? "page" : undefined} data-testid={testId} {...rest}>
      {children}
    </Link>
  );
}

/** "× {brand}" beside the wordmark while a visitor's brand preview is active. */
function PartnerBadge({ partner, light }) {
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [partner?.logoUrl]);
  return (
    <AnimatePresence initial={false}>
      {partner && (
        <motion.span key={partner.domain} initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -6 }} transition={{ duration: 0.4, ease: EASE }} className={cn("hidden xs:inline-flex items-center gap-2 pl-3 ml-1 border-l leading-none", light ? "border-white/15" : "border-ink/15")} data-testid="header-partner">
          <span className={cn("text-[13px]", light ? "text-white/40" : "text-ink/35")} aria-hidden>×</span>
          <span className="flex h-[22px] w-[22px] shrink-0 items-center justify-center overflow-hidden rounded-[6px] text-[11px] font-heading font-bold" style={{ background: partner.logoUrl && !broken ? "transparent" : "var(--demo-brand)", color: "var(--demo-brand-fg)" }}>
            {partner.logoUrl && !broken ? <img src={partner.logoUrl} alt="" onError={() => setBroken(true)} className="h-full w-full object-contain" referrerPolicy="no-referrer" /> : (partner.name || "?").charAt(0).toUpperCase()}
          </span>
          <span className={cn("font-heading font-bold text-[17px] tracking-[-0.03em] max-w-[140px] truncate", light ? "text-white" : "text-ink")} data-testid="header-partner-name">{partner.name}</span>
        </motion.span>
      )}
    </AnimatePresence>
  );
}

export function SiteHeader({ partner = null, cta = null }) {
  const { pathname } = useLocation();
  const onHome = pathname === "/" || pathname.startsWith("/for/") || pathname.startsWith("/preview/");
  const onFeatures = pathname === "/features";
  const sections = onFeatures ? FEATURES_ON_PAGE : ON_PAGE;
  const sectionHref = (id) => (onHome || onFeatures ? `#${id}` : `/#${id}`);
  const [scrolled, setScrolled] = useState(false);
  const [dark, setDark] = useState(false);
  const [open, setOpen] = useState(false);
  const progressRef = useRef(null);
  // Section links in the mobile menu: close the sheet first (it locks body scroll), then scroll.
  const jumpTo = (e, id) => {
    if (!onHome && !onFeatures) return; // another page → let the browser navigate to /#id
    e.preventDefault();
    setOpen(false);
    window.setTimeout(() => {
      const el = document.getElementById(id);
      if (!el) return;
      el.scrollIntoView({ block: "start" });
      window.history.replaceState(window.history.state, "", `#${id}`);
    }, 260);
  };
  useEffect(() => {
    let raf = 0;
    const measure = () => {
      raf = 0;
      setScrolled(window.scrollY > 12);
      // Reading progress — written straight to a CSS var (no React re-render per frame).
      if (progressRef.current) {
        const max = document.documentElement.scrollHeight - window.innerHeight;
        progressRef.current.style.setProperty("--progress", max > 0 ? Math.min(1, window.scrollY / max).toFixed(4) : 0);
      }
      // Header turns dark while it overlaps a dark section (final CTA, footer).
      const line = 32;
      const over = [...document.querySelectorAll('[data-header-theme="dark"]')].some((el) => {
        const r = el.getBoundingClientRect();
        return r.top <= line && r.bottom >= line;
      });
      setDark(over);
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(measure);
    };
    measure();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);
  // Full-width bar. Transparent over the hero; once you scroll it frosts, tightens and
  // gains a hairline + soft shadow.
  return (
    <header className={cn("fixed top-0 left-0 right-0 z-50 border-b transition-[background-color,border-color,color,box-shadow] duration-300 ease-editorial", dark ? "text-white" : "text-ink", scrolled ? (dark ? "bg-ink/95 lg:bg-ink/80 lg:backdrop-blur-xl border-white/[0.08] shadow-[0_12px_32px_-24px_rgba(0,0,0,0.6)]" : "bg-canvas/95 lg:bg-canvas/75 lg:backdrop-blur-xl border-hairline shadow-[0_12px_32px_-24px_rgba(21,19,15,0.25)]") : "bg-transparent border-transparent")} data-testid="site-header" data-theme={dark ? "dark" : "light"} data-scrolled={scrolled ? "true" : "false"}>
      <span ref={progressRef} aria-hidden className={cn("scroll-progress !bottom-auto top-0 transition-opacity duration-300", scrolled ? "opacity-100" : "opacity-0")} data-testid="scroll-progress" />
      <div className={CONTAINER}>
        <div className={cn("flex items-center justify-between gap-8 transition-[height] duration-300 ease-editorial", scrolled ? "h-[60px]" : "h-[68px]")}>
          <Link to="/" className="shrink-0 inline-flex items-center" data-testid="header-logo-link" aria-label={t("brand.flow")}>
            <BrandWordmark size="lg" light={dark} byline={!partner} />
            <PartnerBadge partner={partner} light={dark} />
          </Link>
          <nav className="hidden sm:flex items-center shrink-0" aria-label={t("lp.nav.product")}>
            {/* Pages */}
            <div className="flex items-center gap-6">
              {NAV.map((n) => (
                <NavLink key={n.id} item={n} dark={dark} className={cn("text-[13.5px] font-medium transition-colors", dark ? "text-white/70 hover:text-white" : "text-ink/70 hover:text-ink")} testId={`nav-${n.id}`}>
                  {t(n.key)}
                </NavLink>
              ))}
            </div>
            {/* Account actions — set apart from the pages by a hairline and their own shape */}
            <span aria-hidden className={cn("mx-5 lg:mx-6 h-5 w-px transition-colors duration-300", dark ? "bg-white/15" : "bg-ink/15")} />
            <div className="flex items-center gap-2">
              <Link to="/app/login" className={cn("inline-flex h-[40px] items-center rounded-[9px] border px-3.5 text-[13.5px] font-medium transition-[color,background-color,border-color] duration-150 focus-visible:outline-none focus-visible:shadow-ring", dark ? "border-white/15 text-white/85 hover:bg-white/10 hover:text-white" : "border-hairline bg-surface/60 text-ink hover:bg-fill hover:border-ink/15")} data-testid="home-login-link">{t("lp.login")}</Link>
              {cta ? (
                <PrimaryButton size="sm" className="h-[40px]" onClick={cta.onClick} data-testid="header-cta">{cta.label}</PrimaryButton>
              ) : (
                <Link to="/app/register">
                  <PrimaryButton size="sm" className="h-[40px]" data-testid="header-cta">{t("lp.cta_start")}</PrimaryButton>
                </Link>
              )}
            </div>
          </nav>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button type="button" className={cn("sm:hidden inline-flex items-center justify-center w-11 h-11 -mr-2 rounded-full transition-colors", dark ? "hover:bg-white/10 active:bg-white/15" : "hover:bg-fill active:bg-fill")} aria-label={t("lp.menu")} data-testid="header-mobile-menu-button"><Menu className="h-[18px] w-[18px]" /></button>
            </SheetTrigger>
            {/* A compact sheet from the top: the page's sections, then Pricing · Log in · Get started. */}
            <SheetContent side="top" className="mkt p-0 border-0 bg-canvas rounded-b-[28px] shadow-[0_30px_80px_-30px_rgba(21,19,15,0.35)] [&>button]:hidden max-h-[100dvh] overflow-y-auto" data-testid="mobile-menu" aria-describedby={undefined}>
              <div className="flex flex-col">
                <SheetTitle className="sr-only">{t("lp.menu")}</SheetTitle>
                <div className="h-[68px] px-5 flex items-center justify-between">
                  <Link to="/" onClick={() => setOpen(false)} className="inline-flex items-center" aria-label={t("brand.flow")}><BrandWordmark size="lg" /></Link>
                  <button type="button" onClick={() => setOpen(false)} className="w-11 h-11 -mr-2 rounded-full hover:bg-fill active:bg-fill inline-flex items-center justify-center" aria-label={t("common.close")} data-testid="mobile-menu-close"><X className="h-5 w-5" /></button>
                </div>
                <nav className="px-5 pb-2" aria-label={t("lp.nav.on_page")}>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink/40 pt-1 pb-2">{t("lp.nav.on_page")}</p>
                  <ul className="grid grid-cols-2 gap-x-3">
                    {sections.map(([id, key], i) => (
                      <li key={id} className="fade-up min-w-0" style={{ animationDelay: `${40 + i * 30}ms` }}>
                        <a href={sectionHref(id)} onClick={(e) => jumpTo(e, id)} className="flex items-center justify-between gap-2 h-11 border-b border-hairline text-[15px] font-medium text-ink active:text-ink/60" data-testid={`mobile-nav-${id}`}>
                          <span className="truncate">{t(key)}</span>
                          <ArrowRight className="w-3.5 h-3.5 shrink-0 text-ink/35" />
                        </a>
                      </li>
                    ))}
                    {NAV.map((n, i) => (
                      <li key={n.id} className="fade-up min-w-0" style={{ animationDelay: `${40 + (sections.length + i) * 30}ms` }}>
                        <NavLink item={n} onClick={() => setOpen(false)} className="flex items-center justify-between gap-2 h-11 border-b border-hairline text-[15px] font-medium text-ink active:text-ink/60" testId={`mobile-nav-${n.id}`}>
                          <span className="truncate">{t(n.key)}</span>
                          <ArrowUpRight className="w-3.5 h-3.5 shrink-0 text-ink/35" />
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                </nav>
                <div className="p-5 pt-4 grid grid-cols-2 gap-3 safe-bottom">
                  <Link to="/app/login" className="block min-w-0" onClick={() => setOpen(false)}><SecondaryButton className="w-full" data-testid="mobile-login">{t("lp.login")}</SecondaryButton></Link>
                  {cta ? (
                    <PrimaryButton className="w-full" onClick={() => { setOpen(false); window.setTimeout(() => cta.onClick?.(), 260); }} data-testid="mobile-cta">{cta.label}</PrimaryButton>
                  ) : (
                    <Link to="/app/register" className="block min-w-0" onClick={() => setOpen(false)}><PrimaryButton className="w-full" data-testid="mobile-cta">{t("lp.cta_start")}</PrimaryButton></Link>
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}

export function SiteFooter() {
  const cols = [
    { title: "lp.footer.product", links: [["lp.nav.features", "/features"], ["lp.nav.pricing", "/pricing"], ["lp.login", "/app/login"], ["lp.cta_start", "/app/register"]] },
    { title: "lp.footer.company", links: [["lp.footer.digihome", "https://digihome.no"], ["l4.footer.email", `mailto:${t("l4.footer.email")}`]] },
    { title: "lp.footer.legal", links: [["legal.privacy", "/privacy"], ["legal.terms", "/terms"], ["legal.cookie_settings", "#cookies"]] },
  ];
  const linkCls = "text-[14px] text-white/50 hover:text-white/85 transition-colors";
  const openCookies = (e) => { e.preventDefault(); window.dispatchEvent(new CustomEvent("listed:consent:open")); };
  return (
    <footer className="relative bg-ink text-white border-t border-white/[0.08] grain grain-dark" data-testid="site-footer" data-header-theme="dark">
      <div className={cn(CONTAINER, "relative z-[2]")}>
        <div className="py-12 sm:py-16 grid gap-x-6 gap-y-10 grid-cols-2 lg:grid-cols-6">
          <div className="col-span-2 lg:col-span-2">
            <BrandWordmark light size="lg" />
            <p className="text-[14px] text-white/50 leading-relaxed mt-4 max-w-[36ch]">{t("lp.footer.tagline")}</p>
          </div>
          {cols.map((c) => (
            <div key={c.title} className="min-w-0 lg:col-span-1">
              <h4 className="text-[11.5px] font-semibold text-white/55 uppercase tracking-[0.15em] mb-4 sm:mb-5">{t(c.title)}</h4>
              <ul className="space-y-2.5 sm:space-y-3">
                {c.links.map(([k, h]) => (
                  <li key={k} className="min-w-0">
                    {h === "#cookies" ? <button type="button" onClick={openCookies} className={cn(linkCls, "text-left")} data-testid="footer-cookie-settings">{t(k)}</button>
                      : h.startsWith("http") ? <a href={h} target="_blank" rel="noreferrer" className={linkCls}>{t(k)}</a>
                        : h.startsWith("mailto:") ? <a href={h} className={cn(linkCls, "block truncate")}>{t(k)}</a>
                          : <Link to={h} className={linkCls} data-testid={`footer-link-${h.replace(/\W+/g, "-").replace(/^-|-$/g, "")}`}>{t(k)}</Link>}
                  </li>
                ))}
              </ul>
            </div>
          ))}
          <div className="col-span-2 lg:col-span-1 lg:text-left">
            <h4 className="text-[11.5px] font-semibold text-white/55 uppercase tracking-[0.15em] mb-3 lg:mb-5">{t("l4.footer.contact")}</h4>
            <p className="text-[14px] text-white/50">{t("l4.footer.location")}</p>
          </div>
        </div>
        <div className="py-5 sm:py-6 border-t border-white/[0.08] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-[12px] text-white/40">
          <span>© {new Date().getFullYear()} DigiHome · {t("brand.flow")}</span>
          <span className="flex items-center gap-3">
            <Link to="/privacy" className="hover:text-white/70 transition-colors" data-testid="footer-bottom-privacy">{t("legal.privacy")}</Link>
            <Link to="/terms" className="hover:text-white/70 transition-colors" data-testid="footer-bottom-terms">{t("legal.terms")}</Link>
          </span>
        </div>
      </div>
    </footer>
  );
}
